"use client";

import { cn } from "@marketing-sites/utils/cn";
import * as React from "react";

type NoiseTrailProps = {
  color: string;
  className?: string;
};

type Block = {
  x: number;
  y: number;
  width: number;
  height: number;
  targetWidth: number;
  targetHeight: number;
  age: number; // Track how long the block has been alive
  lifespan: number; // How long before it starts shrinking
  useLargeShape: boolean; // Determined at spawn, never changes
};

// Grid settings (same as cursor trail)
const ROW_HEIGHT = 16;

// Block dimensions
const MAX_WIDTH = 250;
const TARGET_HEIGHT = 12;

// Shape dimensions (matching CursorTrail)
const SMALL_SHAPE_WIDTH = 38;
const SMALL_SHAPE_HEIGHT = 8;
const LARGE_SHAPE_WIDTH = 50;
const LARGE_SHAPE_HEIGHT = 8;
const SHAPE_SIZE_THRESHOLD = 80;

// Animation settings
const ANIMATION_SPEED = 0.04; // Slow, ambient growth

// Spawning settings
const SPAWN_INTERVAL_MIN = 40; // Minimum ms between spawns
const SPAWN_INTERVAL_MAX = 150; // Maximum ms between spawns
const MAX_BLOCKS = 600;
const LIFESPAN_MIN = 3000; // Minimum lifespan in ms
const LIFESPAN_MAX = 7000; // Maximum lifespan in ms

// Clustering settings
const CLUSTER_SPREAD_X = 70; // How far blocks can spawn from cluster center (x)
const CLUSTER_SPREAD_Y = 8; // How many rows up/down from cluster center
const NEW_CLUSTER_CHANCE = 0.08; // Chance to start a new cluster location

// Mouse interaction settings
const MOUSE_CLUSTER_SPREAD_X = 100; // Spread when following mouse
const MOUSE_CLUSTER_SPREAD_Y = 3; // Rows when following mouse
const MOUSE_SPAWN_INTERVAL_MIN = 30; // Much faster spawning when mouse active
const MOUSE_SPAWN_INTERVAL_MAX = 100;
const MOUSE_IDLE_TIMEOUT = 500; // ms before mouse is considered idle

// Block lifecycle
const REMOVAL_THRESHOLD = 1;

/**
 * Drawing functions
 */
const drawHorizontalLines = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  color: string,
) => {
  for (let y = 0; y < canvas.height; y += ROW_HEIGHT) {
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(canvas.width, y + 0.5);
    ctx.lineWidth = 1;
    ctx.strokeStyle = color;
    ctx.stroke();
  }
};

/**
 * Draw small stepped shape (for blocks < 80px wide)
 * Based on SVG: 38x8 with stepped edges
 */
const drawSmallShape = (ctx: CanvasRenderingContext2D, block: Block) => {
  const scaleX = block.width / SMALL_SHAPE_WIDTH;
  const scaleY = block.height / SMALL_SHAPE_HEIGHT;
  const offsetX = block.x - block.width / 2;
  const offsetY = block.y - block.height / 2;

  // Helper to transform SVG coords to canvas coords
  const tx = (x: number) => offsetX + x * scaleX;
  const ty = (y: number) => offsetY + y * scaleY;

  ctx.beginPath();
  // M28.9971 0
  ctx.moveTo(tx(28.9971), ty(0));
  // V1.5
  ctx.lineTo(tx(28.9971), ty(1.5));
  // H33.9971
  ctx.lineTo(tx(33.9971), ty(1.5));
  // V2.75
  ctx.lineTo(tx(33.9971), ty(2.75));
  // H37.9941
  ctx.lineTo(tx(37.9941), ty(2.75));
  // V5.25
  ctx.lineTo(tx(37.9941), ty(5.25));
  // H33.9971
  ctx.lineTo(tx(33.9971), ty(5.25));
  // V6.5
  ctx.lineTo(tx(33.9971), ty(6.5));
  // H28.9971
  ctx.lineTo(tx(28.9971), ty(6.5));
  // V8
  ctx.lineTo(tx(28.9971), ty(8));
  // H8.99707
  ctx.lineTo(tx(8.99707), ty(8));
  // V6.5
  ctx.lineTo(tx(8.99707), ty(6.5));
  // H4
  ctx.lineTo(tx(4), ty(6.5));
  // V5.25
  ctx.lineTo(tx(4), ty(5.25));
  // H0
  ctx.lineTo(tx(0), ty(5.25));
  // V2.75
  ctx.lineTo(tx(0), ty(2.75));
  // H4
  ctx.lineTo(tx(4), ty(2.75));
  // V1.5
  ctx.lineTo(tx(4), ty(1.5));
  // H8.99707
  ctx.lineTo(tx(8.99707), ty(1.5));
  // V0
  ctx.lineTo(tx(8.99707), ty(0));
  // Z (close path back to start)
  ctx.closePath();
  ctx.fill();
};

/**
 * Draw large stepped shape (for blocks >= 80px wide)
 * Based on SVG: 50x8 with simpler stepped edges
 */
const drawLargeShape = (ctx: CanvasRenderingContext2D, block: Block) => {
  const scaleX = block.width / LARGE_SHAPE_WIDTH;
  const scaleY = block.height / LARGE_SHAPE_HEIGHT;
  const offsetX = block.x - block.width / 2;
  const offsetY = block.y - block.height / 2;

  // Helper to transform SVG coords to canvas coords
  const tx = (x: number) => offsetX + x * scaleX;
  const ty = (y: number) => offsetY + y * scaleY;

  ctx.beginPath();
  // M44.9893 0
  ctx.moveTo(tx(44.9893), ty(0));
  // V1.5
  ctx.lineTo(tx(44.9893), ty(1.5));
  // H49.9785
  ctx.lineTo(tx(49.9785), ty(1.5));
  // V6.5
  ctx.lineTo(tx(49.9785), ty(6.5));
  // H44.9893
  ctx.lineTo(tx(44.9893), ty(6.5));
  // V8
  ctx.lineTo(tx(44.9893), ty(8));
  // H4.98926
  ctx.lineTo(tx(4.98926), ty(8));
  // V6.5
  ctx.lineTo(tx(4.98926), ty(6.5));
  // H0
  ctx.lineTo(tx(0), ty(6.5));
  // V1.5
  ctx.lineTo(tx(0), ty(1.5));
  // H4.98926
  ctx.lineTo(tx(4.98926), ty(1.5));
  // V0
  ctx.lineTo(tx(4.98926), ty(0));
  // Z (close path back to start)
  ctx.closePath();
  ctx.fill();
};

/**
 * Draw block shape - uses the shape type determined at spawn time
 */
const drawBlockShape = (ctx: CanvasRenderingContext2D, block: Block) => {
  if (block.useLargeShape) {
    drawLargeShape(ctx, block);
  } else {
    drawSmallShape(ctx, block);
  }
};

const randomBetween = (min: number, max: number) =>
  Math.random() * (max - min) + min;

const snapToRow = (y: number) => Math.floor(y / ROW_HEIGHT) * ROW_HEIGHT;

/**
 * Check if two blocks overlap (using their target dimensions for new blocks)
 */
const blocksOverlap = (
  newX: number,
  newY: number,
  newWidth: number,
  newHeight: number,
  existingBlock: Block,
) => {
  // Use the larger of current or target dimensions for existing blocks
  // to prevent spawning where a growing block will expand into
  const existingWidth = Math.max(
    existingBlock.width,
    existingBlock.targetWidth,
  );
  const existingHeight = Math.max(
    existingBlock.height,
    existingBlock.targetHeight,
  );

  const newLeft = newX - newWidth / 2;
  const newRight = newX + newWidth / 2;
  const newTop = newY - newHeight / 2;
  const newBottom = newY + newHeight / 2;

  const existingLeft = existingBlock.x - existingWidth / 2;
  const existingRight = existingBlock.x + existingWidth / 2;
  const existingTop = existingBlock.y - existingHeight / 2;
  const existingBottom = existingBlock.y + existingHeight / 2;

  // Check for overlap (rectangles overlap if they overlap on both axes)
  return !(
    newRight < existingLeft ||
    newLeft > existingRight ||
    newBottom < existingTop ||
    newTop > existingBottom
  );
};

export const NoiseTrail = ({ color = "black", className }: NoiseTrailProps) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const blocksRef = React.useRef<Block[]>([]);
  const lastSpawnTimeRef = React.useRef(0);
  const nextSpawnDelayRef = React.useRef(
    randomBetween(SPAWN_INTERVAL_MIN, SPAWN_INTERVAL_MAX),
  );
  const lastFrameTimeRef = React.useRef(0);
  const clusterCenterRef = React.useRef<{ x: number; y: number } | null>(null);
  const needsNewClusterRef = React.useRef(true); // Start needing a new cluster

  // Mouse tracking refs
  const mousePositionRef = React.useRef<{ x: number; y: number } | null>(null);
  const isMouseActiveRef = React.useRef(false);
  const mouseIdleTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const getRandomPosition = (canvasWidth: number, canvasHeight: number) => {
    return {
      x: randomBetween(MAX_WIDTH / 2, canvasWidth - MAX_WIDTH / 2),
      y: snapToRow(
        randomBetween(ROW_HEIGHT * 2, canvasHeight - ROW_HEIGHT * 2),
      ),
    };
  };

  const spawnBlock = (canvasWidth: number, canvasHeight: number) => {
    // Use mouse position as cluster center if mouse is active
    const mousePosition = isMouseActiveRef.current
      ? mousePositionRef.current
      : null;
    const isMouseDriven = mousePosition !== null;

    // Check if we should start a new cluster (random chance)
    if (!isMouseDriven && Math.random() < NEW_CLUSTER_CHANCE) {
      needsNewClusterRef.current = true;
    }

    let x: number;
    let y: number;

    if (mousePosition) {
      // Use mouse position as cluster center
      clusterCenterRef.current = {
        x: mousePosition.x,
        y: snapToRow(mousePosition.y),
      };
      needsNewClusterRef.current = false;

      // Use tighter spread when following mouse
      const offsetX = randomBetween(
        -MOUSE_CLUSTER_SPREAD_X,
        MOUSE_CLUSTER_SPREAD_X,
      );
      const offsetRows = Math.floor(
        randomBetween(-MOUSE_CLUSTER_SPREAD_Y, MOUSE_CLUSTER_SPREAD_Y + 1),
      );

      x = Math.max(
        MAX_WIDTH / 2,
        Math.min(
          canvasWidth - MAX_WIDTH / 2,
          clusterCenterRef.current.x + offsetX,
        ),
      );
      y = snapToRow(
        Math.max(
          ROW_HEIGHT,
          Math.min(
            canvasHeight - ROW_HEIGHT,
            clusterCenterRef.current.y + offsetRows * ROW_HEIGHT,
          ),
        ),
      );
    } else if (needsNewClusterRef.current || !clusterCenterRef.current) {
      // First block of a new cluster - spawn at random position and set as cluster center
      const pos = getRandomPosition(canvasWidth, canvasHeight);
      x = pos.x;
      y = pos.y;
      // Cluster center will be set after successful spawn
    } else {
      // Subsequent blocks in cluster - spawn near cluster center
      const cluster = clusterCenterRef.current;
      const offsetX = randomBetween(-CLUSTER_SPREAD_X, CLUSTER_SPREAD_X);
      const offsetRows = Math.floor(
        randomBetween(-CLUSTER_SPREAD_Y, CLUSTER_SPREAD_Y + 1),
      );

      x = Math.max(
        MAX_WIDTH / 2,
        Math.min(canvasWidth - MAX_WIDTH / 2, cluster.x + offsetX),
      );
      y = snapToRow(
        Math.max(
          ROW_HEIGHT,
          Math.min(
            canvasHeight - ROW_HEIGHT,
            cluster.y + offsetRows * ROW_HEIGHT,
          ),
        ),
      );
    }

    // Random target width between 50 and MAX_WIDTH
    const targetWidth = randomBetween(50, MAX_WIDTH);

    // Check if the new block would overlap with any existing blocks
    const wouldOverlap = blocksRef.current.some((existingBlock) =>
      blocksOverlap(x, y, targetWidth, TARGET_HEIGHT, existingBlock),
    );

    // Only spawn if no overlap
    if (!wouldOverlap) {
      blocksRef.current.push({
        x,
        y,
        width: 0,
        height: 0,
        targetWidth,
        targetHeight: TARGET_HEIGHT,
        age: 0,
        lifespan: randomBetween(LIFESPAN_MIN, LIFESPAN_MAX),
        useLargeShape: targetWidth >= SHAPE_SIZE_THRESHOLD,
      });

      // If this was the first block of a new cluster, set its position as the cluster center
      if (needsNewClusterRef.current && !isMouseDriven) {
        clusterCenterRef.current = { x, y };
        needsNewClusterRef.current = false;
      }
    }
  };

  const updateBlocks = (deltaTime: number) => {
    blocksRef.current.forEach((block) => {
      // Update age
      block.age += deltaTime;

      // If past lifespan, start shrinking
      if (block.age >= block.lifespan) {
        block.targetWidth = 0;
        block.targetHeight = 0;
      }

      // Animate towards target
      block.width += (block.targetWidth - block.width) * ANIMATION_SPEED;
      block.height += (block.targetHeight - block.height) * ANIMATION_SPEED;
    });

    // Remove blocks that have fully shrunk
    blocksRef.current = blocksRef.current.filter(
      (block) => block.width > REMOVAL_THRESHOLD,
    );
  };

  const drawBlocks = (ctx: CanvasRenderingContext2D) => {
    blocksRef.current.forEach((block) => {
      ctx.fillStyle = color;
      drawBlockShape(ctx, block);
    });
  };

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };

    const isInsideCanvas = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      return (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      );
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isInsideCanvas(e)) {
        isMouseActiveRef.current = false;
        mousePositionRef.current = null;
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      mousePositionRef.current = {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
      isMouseActiveRef.current = true;

      // Reset idle timeout
      if (mouseIdleTimeoutRef.current) {
        clearTimeout(mouseIdleTimeoutRef.current);
      }
      mouseIdleTimeoutRef.current = setTimeout(() => {
        isMouseActiveRef.current = false;
      }, MOUSE_IDLE_TIMEOUT);
    };

    const handleMouseLeave = () => {
      isMouseActiveRef.current = false;
      mousePositionRef.current = null;
    };

    const render = (currentTime: number) => {
      // Calculate delta time
      const deltaTime = currentTime - lastFrameTimeRef.current;
      lastFrameTimeRef.current = currentTime;

      // Use faster spawn interval when mouse is active
      const spawnIntervalMin = isMouseActiveRef.current
        ? MOUSE_SPAWN_INTERVAL_MIN
        : SPAWN_INTERVAL_MIN;
      const spawnIntervalMax = isMouseActiveRef.current
        ? MOUSE_SPAWN_INTERVAL_MAX
        : SPAWN_INTERVAL_MAX;

      // Check if it's time to spawn a new block
      const timeSinceLastSpawn = currentTime - lastSpawnTimeRef.current;
      if (
        timeSinceLastSpawn >= nextSpawnDelayRef.current &&
        blocksRef.current.length < MAX_BLOCKS
      ) {
        spawnBlock(canvas.width, canvas.height);
        lastSpawnTimeRef.current = currentTime;
        nextSpawnDelayRef.current = randomBetween(
          spawnIntervalMin,
          spawnIntervalMax,
        );
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      updateBlocks(deltaTime);
      drawBlocks(ctx);
      drawHorizontalLines(ctx, canvas, color);
      window.requestAnimationFrame(render);
    };

    // Set initial size
    resizeCanvas();

    // Listen for resize and scroll
    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("scroll", resizeCanvas, { passive: true });

    // Listen for mouse events
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    // Start the render loop
    window.requestAnimationFrame(render);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("scroll", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
      if (mouseIdleTimeoutRef.current) {
        clearTimeout(mouseIdleTimeoutRef.current);
      }
    };
  }, [color]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 block h-full w-full",
        className,
      )}
    />
  );
};
